import { useCallback, useEffect, useState } from "preact/hooks";
import type { SessionResponse } from "../shared/contracts";
import { apiRequest } from "./api";

type SessionStatus = "loading" | "authenticated" | "anonymous" | "error";

export function useSession() {
  const [data, setData] = useState<SessionResponse | null>(null);
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (background = false) => {
    if (!background) {
      setStatus("loading");
      setError(null);
    }
    try {
      const response = await fetch("/api/v1/session", {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (response.status === 401) {
        setData(null);
        setStatus("anonymous");
        return;
      }
      if (!response.ok) throw new Error("Não foi possível verificar sua sessão.");
      setData((await response.json()) as SessionResponse);
      setStatus("authenticated");
    } catch (caught) {
      if (!background) {
        setData(null);
        setError(caught instanceof Error ? caught.message : "Não foi possível verificar sua sessão.");
        setStatus("error");
      }
    }
  }, []);

  useEffect(() => {
    void load();
    const handleExpired = () => {
      setData(null);
      setStatus("anonymous");
    };
    window.addEventListener("powermail:session-expired", handleExpired);
    return () => window.removeEventListener("powermail:session-expired", handleExpired);
  }, [load]);

  const switchTenant = useCallback(async (tenantId: string) => {
    const response = await apiRequest<SessionResponse>("/api/v1/session/workspace", {
      method: "POST",
      body: JSON.stringify({ tenantId }),
    });
    setData(response);
    setStatus("authenticated");
  }, []);

  const switchMailbox = useCallback(async (mailboxId: string) => {
    const response = await apiRequest<SessionResponse>("/api/v1/session/mailbox", {
      method: "POST",
      body: JSON.stringify({ mailboxId }),
    });
    setData(response);
    setStatus("authenticated");
  }, []);

  const logout = useCallback(async () => {
    await apiRequest<void>("/api/v1/auth/logout", { method: "POST" });
    setData(null);
    setStatus("anonymous");
  }, []);

  const retry = useCallback(() => {
    void load();
  }, [load]);

  const refresh = useCallback(() => {
    void load(true);
  }, [load]);

  return {
    data,
    status,
    error,
    retry,
    refresh,
    switchMailbox,
    switchTenant,
    logout,
  };
}
